/**
 * Numeric report slots — the measured values inside a report sentence.
 *
 * These are the counterpart to the phrase dropdowns. A phrase slot offers a
 * clinician a list to pick from; a numeric slot has no vocabulary, so it takes
 * a typed value instead. Both are click-to-edit inside the report text, which
 * is what the clinician asked for: the numbers should be fillable in place
 * rather than requiring the sentence to be retyped (client feedback 18 Aug).
 *
 * Why these are NOT dropdowns: exercise time, METS, heart rates and blood
 * pressure are continuous measured quantities. Offering a fixed list would
 * either be wrong for most studies or so long it is slower than typing.
 *
 * The key is the template placeholder name, e.g. `{{stress_mets}}`. Values
 * arrive pre-filled from the CardioSoft XML when a study has one; when it does
 * not (the common case today) the slot shows a `--` marker the clinician
 * clicks and types into.
 *
 * `unit` is the text already printed in the template AFTER the placeholder —
 * it is shown in the edit popup as a hint only, never inserted into the report,
 * otherwise the unit would appear twice.
 */
export type NumericFieldSpec = {
  /** Clinical name shown as the popup's title. */
  label: string;
  /** Unit printed beside the value in the template. Display hint only. */
  unit: string;
  /** Example of the expected form, shown as the input's placeholder. */
  placeholder: string;
};

export const NUMERIC_REPORT_FIELDS: Record<string, NumericFieldSpec> = {
  stress_exercise_minutes: { label: 'Exercise Time', unit: 'minutes', placeholder: '8:00' },
  stress_mets: { label: 'Exercise Workload', unit: 'METS', placeholder: '10.3' },
  stress_percent_predicted: {
    label: '% of Predicted Maximum HR',
    unit: '%',
    placeholder: '94',
  },
  stress_resting_hr: { label: 'Resting Heart Rate', unit: 'BPM', placeholder: '82' },
  stress_peak_hr: { label: 'Peak Heart Rate', unit: 'BPM', placeholder: '156' },
  stress_target_hr: { label: 'Target (Predicted Max) Heart Rate', unit: 'BPM', placeholder: '166' },
  // Blood pressure is kept free-text rather than numeric: it is conventionally
  // reported as systolic/diastolic ("180/70"), which a number input would reject.
  stress_resting_bp: { label: 'Resting Blood Pressure', unit: 'mmHg', placeholder: '120/80' },
  stress_peak_bp: { label: 'Peak Blood Pressure', unit: 'mmHg', placeholder: '180/70' },
  /**
   * Legacy alias for `stress_peak_bp`, kept because 31 clinic templates already
   * carry `{{stress_peak_sbp}}` in their text. Renaming it in the database would
   * mean rewriting a clinical sentence in every one of them for no clinical gain.
   * It resolves to the same full systolic/diastolic pair, which is what its label
   * always claimed — the `sbp` in the name was the mistake, not the label.
   * New templates should use `stress_peak_bp`.
   */
  stress_peak_sbp: { label: 'Peak Blood Pressure', unit: 'mmHg', placeholder: '180/70' },
};

/** Marker shown when a slot has no value yet. Clicking it opens the editor. */
export const NUMERIC_EMPTY_MARKER = '--';

export const isNumericFieldKey = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(NUMERIC_REPORT_FIELDS, key);

/**
 * Wrap a resolved value in the click-to-edit span.
 *
 * Escapes the value because it originates from parsed XML, not from us. The
 * key is a fixed map key, so it needs no escaping.
 */
export const wrapNumericField = (key: string, value: string): string => {
  const spec = NUMERIC_REPORT_FIELDS[key];
  if (!spec) {
    return value;
  }
  const shown = value && value.trim() ? value : NUMERIC_EMPTY_MARKER;
  const safe = shown
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return `<span data-editable-number class="${key}">${safe}</span>`;
};

/**
 * Keys that stand for the SAME measured value under different placeholder names.
 *
 * Only one group exists today: `stress_peak_bp` and its legacy twin
 * `stress_peak_sbp` (see the note on the field above). A template carries one or
 * the other, but a clinic that has both — or a template mid-rename — must not be
 * able to show two different peak pressures in one report.
 *
 * This is deliberately NOT a general synonym table. Two keys belong in a group
 * only when they are the same quantity in the same unit, so writing one into the
 * other can never change meaning.
 */
const NUMERIC_FIELD_ALIAS_GROUPS: string[][] = [['stress_peak_bp', 'stress_peak_sbp']];

/**
 * Every key that must carry the same value as `key`, including `key` itself.
 *
 * Used when a clinician commits a value: the typed figure goes to all
 * occurrences of the key they clicked AND to every alias of it.
 */
export const numericAliasGroup = (key: string): string[] => {
  const group = NUMERIC_FIELD_ALIAS_GROUPS.find(g => g.includes(key));
  return group ? [...group] : [key];
};

/**
 * Is this entry finished, or still mid-token?
 *
 * Looser than a plain number check because these fields are not all numbers: blood
 * pressure is "110/80" and exercise time is "8:30". A value still sitting on its
 * separator ("110/", "8:", "13.") is half-typed and must not be committed.
 */
export const isCompleteNumericEntry = (value: unknown): boolean => {
  const text = String(value ?? '').trim();
  if (!text || !/\d/.test(text)) {
    return false;
  }
  return !/[./:+-]$/.test(text);
};
